/**
 * Sorting game — one or two attributes, always framed as a hunt.
 *
 * Cycle 1 recorded the lesson this schema encodes: two-attribute sorting failed
 * as an abstract task and works as a game with a job in it. `framing` is
 * therefore REQUIRED, not optional — there is no way to emit a bare "sort these
 * by colour" activity from this spec.
 */

import { z } from 'zod'
import { AttributesSchema, EmojiSchema, IdSchema, LabelSchema, SeedSchema, SentenceSchema, isUnique, specFor } from '../common'

export const HUNT_SCENARIOS = ['treasure_hunt', 'animal_clinic', 'rescue_mission', 'grocery_run', 'custom'] as const

export const SortingFramingSchema = z.object({
  scenario: z.enum(HUNT_SCENARIOS),
  /** The job, in her words: "The sick animals need the right room!" */
  storyPrompt: SentenceSchema,
  /** What she is doing: "rescue", "find", "carry". */
  huntVerb: LabelSchema.max(24),
  /** What a bin is called in this story: "room", "jar", "bay". */
  binNoun: LabelSchema.max(24),
})

export const SortingItemSchema = z.object({
  id: IdSchema,
  label: LabelSchema,
  emoji: EmojiSchema.optional(),
  attributes: AttributesSchema,
})

export const SortingBinSchema = z.object({
  id: IdSchema,
  label: LabelSchema,
  emoji: EmojiSchema.optional(),
  /** The exact attribute values this bin accepts — one entry per criterion. */
  match: AttributesSchema,
})

export const SortingGameConfigSchema = z
  .object({
    framing: SortingFramingSchema,
    items: z.array(SortingItemSchema).min(4).max(24),
    /** One attribute is solid; two is the Cycle-2 target. */
    criteria: z.array(LabelSchema.max(40)).min(1).max(2),
    bins: z.array(SortingBinSchema).min(2).max(6),
    /** A hunt hands over one find at a time. */
    revealItemsOneAtATime: z.boolean().default(true),
    shuffleSeed: SeedSchema.default(1),
    /** A wrong drop bounces back with a hint rather than a buzzer. */
    gentleCorrection: z.boolean().default(true),
  })
  .superRefine((config, ctx) => {
    if (!isUnique(config.criteria)) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['criteria'], message: 'list each criterion once' })
      return
    }
    if (!isUnique(config.items.map((item) => item.id))) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['items'], message: 'item ids must be unique' })
    }
    if (!isUnique(config.bins.map((bin) => bin.id))) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['bins'], message: 'bin ids must be unique' })
    }

    for (const [index, bin] of config.bins.entries()) {
      const keys = Object.keys(bin.match)
      const missing = config.criteria.filter((criterion) => !keys.includes(criterion))
      const extra = keys.filter((key) => !config.criteria.includes(key))
      if (missing.length > 0 || extra.length > 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['bins', index, 'match'],
          message: `a bin must match on exactly the criteria (${config.criteria.join(', ')})`,
        })
      }
    }
    const binKeys = config.bins.map((bin) => config.criteria.map((criterion) => bin.match[criterion]).join('|'))
    if (!isUnique(binKeys)) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['bins'], message: 'two bins accept the same thing' })
    }

    const counts = new Map<string, number>()
    for (const [index, item] of config.items.entries()) {
      const missing = config.criteria.filter((criterion) => item.attributes[criterion] === undefined)
      if (missing.length > 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['items', index, 'attributes'],
          message: `"${item.label}" is missing ${missing.join(', ')}, so it belongs nowhere`,
        })
        continue
      }
      const matching = config.bins.filter((bin) =>
        config.criteria.every((criterion) => bin.match[criterion] === item.attributes[criterion]),
      )
      if (matching.length !== 1) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['items', index],
          message:
            matching.length === 0
              ? `"${item.label}" fits no ${config.framing.binNoun}`
              : `"${item.label}" fits ${matching.length} of them`,
        })
        continue
      }
      counts.set(matching[0].id, (counts.get(matching[0].id) ?? 0) + 1)
    }
    for (const [index, bin] of config.bins.entries()) {
      if (!counts.has(bin.id)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['bins', index],
          message: `nothing ever goes in "${bin.label}"`,
        })
      }
    }
  })

export type SortingGameConfig = z.infer<typeof SortingGameConfigSchema>

export const SortingGameSpecSchema = specFor('sorting_game', SortingGameConfigSchema)
